/**
 * ChatlogImportDialog — Paste a CoH: Homecoming chat log, get a dry-run
 * preview, then commit the badge unlocks.
 *
 * The backend handles all parsing; this UI just posts the raw text with
 * `dryRun: true` first, renders the summary, then re-posts with
 * `dryRun: false` when the user confirms.
 *
 * "Fallback character" — a single log can span multiple alts (Welcome
 * lines mark each character-switch). If the paste is a partial log
 * where the FIRST Welcome line was scrolled out of the chat buffer,
 * the user can pick a fallback character; it's applied ONLY to events
 * before the first Welcome line. Once a Welcome line appears, the log's
 * own character segmenting takes over, so mid-log alt-switches still
 * resolve correctly.
 */
import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, AlertCircle, CheckCircle2, UserPlus2, HelpCircle, X, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const AUTO_DETECT = "__auto__";

export function ChatlogImportDialog({ open, onOpenChange, gameId, api, onImported, characters = [] }) {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [fallbackCharacterId, setFallbackCharacterId] = useState(AUTO_DETECT);

  const reset = () => {
    setText("");
    setPreview(null);
    setFallbackCharacterId(AUTO_DETECT);
  };

  const fallbackName = useMemo(() => {
    if (fallbackCharacterId === AUTO_DETECT) return null;
    const c = characters.find((n) => n.id === fallbackCharacterId);
    return c ? (c.title || c.name || "").trim() : null;
  }, [fallbackCharacterId, characters]);

  const runPreview = async () => {
    if (!text.trim()) {
      toast.error("Paste a chat log first");
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(
        `${api}/games/${gameId}/homecoming/import-chatlog`,
        { text, dryRun: true, fallbackCharacterName: fallbackName },
      );
      setPreview(res.data);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const msg = detail
        || (status ? `Preview failed (HTTP ${status}${status === 413 ? " — log too large" : status >= 500 ? " — server error" : ""})`
                   : `Preview failed — ${e.message || "network error"}`);
      console.error("Chatlog preview failed:", status, e.response?.data || e.message);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    try {
      setCommitting(true);
      const res = await axios.post(
        `${api}/games/${gameId}/homecoming/import-chatlog`,
        { text, dryRun: false, fallbackCharacterName: fallbackName },
      );
      const total = res.data.achievementsUpdated || 0;
      toast.success(`Imported! ${total} badge${total === 1 ? "" : "s"} updated.`);
      onImported && onImported();
      onOpenChange(false);
      reset();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const msg = detail
        || (status ? `Import failed (HTTP ${status}${status === 413 ? " — log too large" : status >= 500 ? " — server error" : ""})`
                   : `Import failed — ${e.message || "network error"}`);
      console.error("Chatlog import failed:", status, e.response?.data || e.message);
      toast.error(msg);
    } finally {
      setCommitting(false);
    }
  };

  const summary = useMemo(() => {
    if (!preview) return null;
    const totalWrites =
      preview.perCharacter?.reduce(
        (n, c) => n + (c.earnedNew || 0) + (c.titleFilled || 0),
        0,
      ) || 0;
    return { totalWrites };
  }, [preview]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl w-[95vw] max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="chatlog-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-cyan-400" />
            Upload Homecoming Chat Log
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Paste the contents of a chat log file. Badges the game recorded
            (both{" "}
            <span className="text-white/80">earned</span> and{" "}
            <span className="text-white/80">selected as new title</span>) are
            attributed to whichever character was active at that timestamp.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pt-2 space-y-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste chat log lines here..."
            className="min-h-[180px] bg-black/50 border-white/10 text-xs font-mono"
            data-testid="chatlog-import-textarea"
          />

          {/* Fallback character — attributed only to events before the first Welcome line */}
          <div className="p-2.5 rounded border border-white/10 bg-black/30 space-y-1.5">
            <label className="text-xs text-white/70 flex items-center gap-1.5">
              <User className="w-3.5 h-3.5" />
              Fallback character (for events before first session-start line):
            </label>
            <Select value={fallbackCharacterId} onValueChange={setFallbackCharacterId}>
              <SelectTrigger
                className="bg-black/50 border-white/10 text-xs h-8"
                data-testid="chatlog-fallback-character-trigger"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10 text-white">
                <SelectItem value={AUTO_DETECT} data-testid="chatlog-fallback-character-none">
                  None (skip orphan events)
                </SelectItem>
                {characters.map((c) => (
                  <SelectItem
                    key={c.id}
                    value={c.id}
                    data-testid={`chatlog-fallback-character-${c.id}`}
                  >
                    {(c.title || c.name || "Untitled").trim()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fallbackName && (
              <p className="text-[11px] text-cyan-300/80">
                Events before the first session-start line (
                <code>Welcome to City of Heroes…</code> for heroes,{" "}
                <code>Now entering the Rogue Isles…</code> for villains) will be
                attributed to <span className="text-white">{fallbackName}</span>. Session-start
                lines in the log still switch attribution mid-log, so multi-alt sessions resolve
                correctly.
              </p>
            )}
            {!fallbackName && characters.length === 0 && (
              <p className="text-[11px] text-amber-300/70">
                No character notes yet on this game — create one first if your log doesn&apos;t include a session-start line.
              </p>
            )}
          </div>

          {!preview && (
            <Button
              onClick={runPreview}
              disabled={loading || !text.trim()}
              className="w-full bg-cyan-600 hover:bg-cyan-700"
              data-testid="chatlog-preview-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Preview
            </Button>
          )}

          {preview && (
            <div className="space-y-3">
              <div className="p-3 rounded border border-cyan-500/30 bg-cyan-500/5">
                <p className="text-sm text-white">
                  <span className="text-cyan-300 font-medium">{preview.totalEventsParsed}</span>{" "}
                  events parsed across{" "}
                  <span className="text-cyan-300 font-medium">
                    {preview.charactersInLog?.length || 0}
                  </span>{" "}
                  character{preview.charactersInLog?.length === 1 ? "" : "s"}. Ready to write{" "}
                  <span className="text-emerald-300 font-medium">{summary.totalWrites}</span> badge unlock
                  {summary.totalWrites === 1 ? "" : "s"}.
                </p>
                {preview.orphanEventCount > 0 && !fallbackName && (
                  <p className="text-xs text-amber-300/80 mt-1">
                    {preview.orphanEventCount} event(s) before the first session-start line have no
                    known character and were skipped. Include the{" "}
                    <code>Welcome to City of Heroes…</code> or{" "}
                    <code>Now entering the Rogue Isles…</code> line, or pick a fallback character above.
                  </p>
                )}
              </div>

              {preview.perCharacter?.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs uppercase text-white/50 tracking-wider">Per-character</p>
                  {preview.perCharacter.map((c) => (
                    <div
                      key={c.characterName}
                      className={`p-2 rounded border text-xs flex flex-wrap items-center gap-2 ${
                        c.hasCharacterNote
                          ? "border-white/10 bg-black/40"
                          : "border-amber-500/30 bg-amber-500/5"
                      }`}
                      data-testid={`chatlog-preview-char-${c.characterName}`}
                    >
                      <span className="text-white font-medium">{c.characterName}</span>
                      {c.hasCharacterNote ? (
                        <>
                          <span className="text-emerald-300">+{c.earnedNew} new earns</span>
                          <span className="text-cyan-300">+{c.titleFilled} title-only</span>
                          {c.titleSkipped > 0 && (
                            <span className="text-white/40">{c.titleSkipped} title skipped (already dated)</span>
                          )}
                          {c.unknownBadgeCount > 0 && (
                            <span className="text-amber-300">
                              {c.unknownBadgeCount} unknown badge{c.unknownBadgeCount === 1 ? "" : "s"}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-amber-300 flex items-center gap-1">
                          <UserPlus2 className="w-3 h-3" />
                          No character note yet — events skipped
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {preview.missingCharacterNotes?.length > 0 && (
                <div className="p-2.5 rounded border border-amber-500/40 bg-amber-500/10 text-xs">
                  <p className="font-medium text-amber-200 flex items-center gap-1 mb-1">
                    <AlertCircle className="w-3.5 h-3.5" /> Missing character notes
                  </p>
                  <p className="text-amber-100/80 mb-1">
                    Create a character note for each of these names on the game page, then re-upload
                    to pick up the skipped events:
                  </p>
                  <ul className="list-disc pl-5 text-white/80">
                    {preview.missingCharacterNotes.map((m) => (
                      <li key={m.characterName}>
                        <span className="text-white">{m.characterName}</span>{" "}
                        <span className="text-white/50">— {m.eventsSkipped} event(s)</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {preview.badgesNotInLibrary?.length > 0 && (
                <div className="p-2.5 rounded border border-sky-500/40 bg-sky-500/10 text-xs">
                  <p className="font-medium text-sky-200 flex items-center gap-1 mb-1">
                    <AlertCircle className="w-3.5 h-3.5" /> Badges not in your library
                  </p>
                  <p className="text-sky-100/80 mb-1">
                    These badges exist in the Master DB but you haven&apos;t added them to your library
                    yet (or they&apos;re in a DLC you haven&apos;t linked). Add them from Master DB first, then
                    re-run:
                  </p>
                  <ul className="list-disc pl-5 text-white/80 max-h-32 overflow-y-auto">
                    {preview.badgesNotInLibrary.map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {preview.unknownBadges?.length > 0 && (
                <div className="p-2.5 rounded border border-purple-500/40 bg-purple-500/10 text-xs">
                  <p className="font-medium text-purple-200 flex items-center gap-1 mb-1">
                    <HelpCircle className="w-3.5 h-3.5" /> Unknown badges (not yet in the database)
                  </p>
                  <p className="text-purple-100/80 mb-1">
                    These will be added to your review queue so you can create them in the Master
                    Database:
                  </p>
                  <ul className="list-disc pl-5 text-white/80 max-h-32 overflow-y-auto">
                    {preview.unknownBadges.map((u) => (
                      <li key={u.badgeName}>
                        <span className="text-white">{u.badgeName}</span>{" "}
                        <span className="text-white/50">
                          — seen {u.timesSeen}× by {u.characters.join(", ")}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setPreview(null)}
                  className="text-white/70 hover:text-white"
                >
                  Back
                </Button>
                <Button
                  onClick={commit}
                  disabled={committing || summary.totalWrites === 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="chatlog-commit-btn"
                >
                  {committing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Import{summary.totalWrites > 0 && ` ${summary.totalWrites} unlock${summary.totalWrites === 1 ? "" : "s"}`}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end pt-3 border-t border-white/10">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-white/70 hover:text-white"
          >
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ChatlogImportDialog;
